/**
 * #45 Destek Talebi detayı — destek personelinin yazışma ekranı.
 *
 * Form DEĞİL, yazışma ekranıdır → DetailShell `modeless` kullanılır. Akışta kullanıcı, AI ve
 * destek mesajları BİRLİKTE görünür: personel devraldığında AI'ın ne söylediğini görmeden
 * cevap yazmak yanlış yönlendirmeye yol açar.
 *
 * Aksiyonlar: üstlen (assign), cevap yaz (reply), durum/öncelik değiştir (status).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import { DetailShell, Section, Grid } from './detail/DetailShell';
import { STATUS_LABELS, PRIORITY_LABELS, CATEGORY_LABELS } from './SupportTicketsPage';
import './supportTickets.css';

const API_BASE = '/api/supporttickets';
const LIST_ROUTE = '/support-tickets';

// SupportMessageSender (sunucu) ile birebir.
const SENDER = { USER: 0, AI: 1, AGENT: 2, SYSTEM: 3 };

const SENDER_LABEL = {
  [SENDER.USER]: 'Kullanıcı',
  [SENDER.AI]: 'Rona.AI',
  [SENDER.AGENT]: 'Destek',
  [SENDER.SYSTEM]: 'Sistem',
};

// Durum değişikliği hazır butonlarla yapılır (Çözüldü / Kapat) — açılır liste yok.
const PRIORITY_OPTIONS = Object.entries(PRIORITY_LABELS).map(([value, label]) => ({ value: Number(value), label }));

const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('tr-TR') : '-');

export function SupportTicketDetailPage() {
  const { id } = useParams();
  const { canEdit } = usePageActions();
  usePageMeta('Destek Talebi', 'Kullanıcı ile yazışma ve talep yönetimi.');

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [reply, setReply] = useState('');
  const [toast, setToast] = useState(null);
  const threadEndRef = useRef(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async (markRead = false) => {
    try {
      const res = await fetch(`${API_BASE}/${id}`, { headers: authHeaders() });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(payload?.message || payload?.Message || 'Talep yüklenemedi.');
      const data = payload?.data ?? payload?.Data ?? payload;
      setTicket(data);

      // Ekranı açan personel talebi okumuş sayılır → kuyruk rozeti düşer.
      if (markRead && data?.unreadByAgent && canEdit) {
        fetch(`${API_BASE}/${id}/read`, { method: 'POST', headers: authHeaders() }).catch(() => {});
      }
    } catch (e) {
      showToast('error', e.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, canEdit]);

  useEffect(() => { load(true); }, [load]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ block: 'end' });
  }, [ticket?.messages?.length]);

  async function post(path, body, successMessage) {
    setSending(true);
    try {
      const res = await fetch(`${API_BASE}/${id}/${path}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body ?? {}),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok || payload?.success === false || payload?.Success === false) {
        throw new Error(payload?.message || payload?.Message || 'İşlem tamamlanamadı.');
      }
      showToast('success', successMessage);
      await load();
      return true;
    } catch (e) {
      showToast('error', e.message);
      return false;
    } finally {
      setSending(false);
    }
  }

  async function handleReply() {
    const message = reply.trim();
    if (!message) return;
    const ok = await post('reply', { message }, 'Cevap gönderildi; kullanıcıya bildirim gitti.');
    if (ok) setReply('');
  }

  const isClosed = ticket && !ticket.isOpen;

  return (
    <DetailShell
      modeless
      loading={loading}
      crumb={[
        { label: 'Destek Talepleri', to: LIST_ROUTE },
        { label: ticket ? `#${ticket.ticketNo}` : 'Talep', current: true },
      ]}
      status={ticket ? { label: STATUS_LABELS[ticket.status] || '-' } : null}
      actions={canEdit && ticket && (
        <>
          {!ticket.assignedUserName && (
            <button type="button" className="dps-btn primary" disabled={sending}
              onClick={() => post('assign', {}, 'Talebi üstlendiniz.')}>
              Ben İlgileneceğim
            </button>
          )}
          {ticket.isOpen && (
            <button type="button" className="dps-btn" disabled={sending}
              onClick={() => post('status', { status: 4 }, 'Talep çözüldü olarak işaretlendi.')}>
              Çözüldü
            </button>
          )}
          {ticket.isOpen && (
            <button type="button" className="dps-btn" disabled={sending}
              onClick={() => post('status', { status: 5 }, 'Talep kapatıldı.')}>
              Kapat
            </button>
          )}
        </>
      )}
    >
      {ticket && (
        <div className="dps-form">
          <Section icon="info" title={ticket.subject || 'Destek talebi'}
            subtitle={`${ticket.userFullName || 'Kullanıcı'} · ${fmtDateTime(ticket.createdDate)}`}>
            <Grid cols={3}>
              <ReadField label="Talep No" value={`#${ticket.ticketNo}`} mono />
              <ReadField label="Durum" value={STATUS_LABELS[ticket.status] || '-'} />
              <ReadField label="Öncelik" value={PRIORITY_LABELS[ticket.priority] || '-'} />
              <ReadField label="Kategori" value={CATEGORY_LABELS[ticket.category] || ticket.category || '-'} />
              <ReadField label="İlgilenen" value={ticket.assignedUserName || '— atanmadı'} />
              <ReadField label="Geldiği Ekran" value={ticket.screenTitle || ticket.screenCode || '-'} />
              <ReadField label="Platform" value={ticket.platform || '-'} />
              <ReadField label="Uygulama Sürümü" value={ticket.appVersion || '-'} mono />
              <ReadField label="Son Hareket" value={fmtDateTime(ticket.lastMessageDate)} />
            </Grid>

            {canEdit && (
              <div className="sup-priority-row">
                <span className="sup-muted">Öncelik değiştir:</span>
                {PRIORITY_OPTIONS.map((opt) => (
                  <button key={opt.value} type="button" disabled={sending || ticket.priority === opt.value}
                    className={`dps-btn${ticket.priority === opt.value ? ' primary' : ''}`}
                    onClick={() => post('status', { priority: opt.value }, 'Öncelik güncellendi.')}>
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </Section>

          <Section icon="list" title="Yazışma"
            subtitle="Kullanıcı, AI ve destek mesajları aynı akışta. AI mesajları devir öncesini gösterir.">
            <div className="sup-thread">
              {(ticket.messages || []).map((m) => (
                <div key={m.id} className={`sup-msg sup-msg--${senderClass(m.senderKind)}`}>
                  <div className="sup-msg-head">
                    <span className="sup-msg-sender">{m.senderName || SENDER_LABEL[m.senderKind] || '-'}</span>
                    <span className="sup-msg-time">{fmtDateTime(m.createdDate)}</span>
                  </div>
                  <div className="sup-msg-body">{m.content}</div>
                </div>
              ))}
              {(ticket.messages || []).length === 0 && (
                <div className="sup-muted">Bu talepte henüz mesaj yok.</div>
              )}
              <div ref={threadEndRef} />
            </div>

            {canEdit && (
              <div className="sup-reply">
                <textarea
                  className="sup-reply-input"
                  rows={3}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder={isClosed
                    ? 'Kapanmış talebe yazarsanız talep yeniden açılır…'
                    : 'Kullanıcıya cevabınızı yazın…'}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleReply();
                  }}
                />
                <div className="sup-reply-actions">
                  <span className="sup-muted">Ctrl+Enter ile gönder — kullanıcıya bildirim gider.</span>
                  <button type="button" className="dps-btn primary" disabled={sending || !reply.trim()}
                    onClick={handleReply}>
                    {sending ? 'Gönderiliyor…' : 'Cevap Gönder'}
                  </button>
                </div>
              </div>
            )}

            {!canEdit && (
              <div className="sup-muted">Bu talebe cevap yazmak için düzenleme yetkisi gerekir.</div>
            )}
          </Section>
        </div>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

/** DetailShell'in Field* bileşenleri forma bağlıdır; burada salt-okunur gösterim yeterli. */
function ReadField({ label, value, mono }) {
  return (
    <div className="sup-read-field">
      <div className="sup-read-label">{label}</div>
      <div className={`sup-read-value${mono ? ' sup-mono' : ''}`}>{value}</div>
    </div>
  );
}

function senderClass(kind) {
  if (kind === SENDER.USER) return 'user';
  if (kind === SENDER.AI) return 'ai';
  if (kind === SENDER.AGENT) return 'agent';
  return 'system';
}

export default SupportTicketDetailPage;
